import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { ErrorState } from '@subscriptions/presentation/components/base';
import { withWizardOrigin } from '@subscriptions/presentation/components/specific/BlockCreationWizard/wizardOrigin';
import { resolveSubscriptionErrorMessageByCode } from '@subscriptions/presentation/utils';

type CheckoutErrorConfirmationProps = {
  blockUuid: string;
  errorCode?: string;
};

const FALLBACK_KEY = 'blockCreationWizard.confirmation.error.message';

/**
 * Terminal error confirmation (wizard step 5, `error` variant).
 *
 * Rendered inside the wizard layout (never a toast) when the CARD
 * `onComplete`/`confirm-session` step failed. The raw backend `errorCode` carried
 * in route state is mapped to a translated message via {@link resolveSubscriptionErrorMessageByCode}
 * — backend codes double as i18n keys under the `subscriptions` namespace. When the
 * code is absent or has no registered translation, the mapper returns the generic
 * fallback copy. The retry action sends the user back to the method-selection
 * step (step 3, `/payment`), carrying the entry origin: without it the retried
 * attempt would resolve a plain charge instead of a reactivation.
 *
 * @param props.blockUuid - Subscription block uuid for the in-flight wizard
 * @param props.errorCode - Raw backend error code carried from the failed confirm-session
 * @returns React element rendering the checkout error confirmation
 */
export const CheckoutErrorConfirmation = ({ blockUuid, errorCode }: CheckoutErrorConfirmationProps) => {
  const { t, i18n } = useTranslation('subscriptions');
  const history = useHistory();
  const { search } = useLocation();

  const description = resolveSubscriptionErrorMessageByCode(errorCode, t, i18n, FALLBACK_KEY);

  const handleRetry = () => {
    history.push(withWizardOrigin(`/subscriptions/blocks/create/${blockUuid}/payment`, search));
  };

  return (
    <div className="my-auto flex w-full flex-col items-center gap-6 p-6 font-sans">
      <ErrorState
        bordered={false}
        description={description}
        onRetry={handleRetry}
        retryLabel={t('blockCreationWizard.confirmation.error.retryAction')}
        title={t('blockCreationWizard.confirmation.error.title')}
      />
    </div>
  );
};
